import { Info, Wallet } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '@/components/empty-state';
import { MetricCard, MetricGrid } from '@/components/metric-card';
import { MoneyValue } from '@/components/money-value';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Page } from '@/components/shell/page';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrderPlatforms } from '@/features/orders/api/orders';
import { PlatformMark } from '@/features/orders/components/platform-mark';
import { formatMoney } from '@/lib/money';
import { useReceivable } from '../api/finance';

const ALL = 'todas';

/** Dia `AAAA-MM-DD` (já no fuso da empresa) por extenso curto: "qua., 01/10". */
const dayLabel = (day: string) =>
  new Date(`${day}T12:00:00`).toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });

/**
 * A receber (ADR-050): vendas pagas cujo dinheiro ainda não entrou — com data de liberação, por
 * dia; sem data ainda, porque a plataforma só define depois da entrega. O valor é o recebido
 * líquido esperado (receita menos tarifa e frete conhecidos).
 */
export function ReceivablePage() {
  const platforms = useOrderPlatforms();
  const [tab, setTab] = useState(ALL);
  const receivable = useReceivable(tab === ALL ? {} : { provider: tab });
  return (
    <Page size="lg">
      <PageHeader
        title="A receber"
        description="Vendas pagas cujo dinheiro a plataforma ainda não liberou, pelo dia em que vai entrar."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value={ALL}>Todas</TabsTrigger>
          {(platforms.data ?? []).map((platform) => (
            <TabsTrigger key={platform.provider} value={platform.provider} className="gap-2">
              <PlatformMark provider={platform.provider} />
              {platform.displayName}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <QueryState
        query={receivable}
        isEmpty={(data) => data.orders === 0}
        empty={
          <EmptyState
            icon={Wallet}
            title="Nada a receber"
            description="Todo o dinheiro das vendas pagas já foi liberado."
          />
        }
      >
        {(data) => (
          <div className="space-y-6">
            {data.releaseDatePending > 0 && (
              <Alert>
                <Info aria-hidden />
                <AlertDescription>
                  {data.releaseDatePending} vendas já entregues ainda estão com a data de liberação
                  sendo consultada na plataforma. Elas não entram no total: venda entregue há
                  semanas já foi liberada.
                </AlertDescription>
              </Alert>
            )}
            <MetricGrid className="lg:grid-cols-3">
              <MetricCard
                label="A receber"
                value={formatMoney(data.total)}
                hint={`${data.orders} vendas`}
              />
              <MetricCard
                label="Com data marcada"
                value={formatMoney(data.scheduledTotal.amount)}
                hint={`${data.scheduledTotal.orders} vendas`}
              />
              <MetricCard
                label="Aguardando entrega"
                value={formatMoney(data.awaitingDelivery.amount)}
                hint={`${data.awaitingDelivery.orders} vendas a enviar ou em trânsito — a data sai depois da entrega`}
              />
            </MetricGrid>
            <Card>
              <CardHeader>
                <CardTitle>Por dia de liberação</CardTitle>
              </CardHeader>
              <CardContent>
                {data.scheduled.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma liberação marcada ainda.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dia</TableHead>
                        <TableHead className="text-right">Vendas</TableHead>
                        <TableHead className="text-right">Valor líquido</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.scheduled.map((day) => (
                        <TableRow key={day.day}>
                          <TableCell>{dayLabel(day.day)}</TableCell>
                          <TableCell className="tabular text-right">{day.orders}</TableCell>
                          <TableCell className="text-right">
                            <MoneyValue value={day.amount} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </QueryState>
    </Page>
  );
}
